/**
 * Wire shapes for the message-gateway `/knowledge` REST surface.
 *
 * Defined as Zod schemas so the queryFns can parse responses at the trust
 * boundary (HTTP response → TS) without a separate type definition. Static
 * types fall out of `z.infer` so the schema and the type cannot drift.
 *
 * Co-located with the only consumer (the citation chip → dialog flow). When
 * a second feature needs to read the corpus, this moves to a shared lib —
 * see frontend CLAUDE.md, "compose, don't fragment".
 */

import { z } from 'zod';

export const KnowledgeKindSchema = z.enum([
  'data_finding',
  'clinical_def',
  'cohort_function',
  'snippet',
  'study',
  'av',
]);

export const KnowledgeScopeSchema = z.enum(['omop', 'global', 'dataset', 'session', 'user', 'org']);

/**
 * Identity-bound scopes that an analytical variable moves through during sharing. The
 * promotion endpoint only accepts these three; the wider knowledge-tier
 * scopes are seeded / curated and never appear on agent-authored analytical variables.
 */
export const AvPromotionScopeSchema = z.enum(['session', 'user', 'org']);
export type AvPromotionScope = z.infer<typeof AvPromotionScopeSchema>;

export const AvPromoteResultSchema = z.object({
  slug: z.string(),
  previousScope: AvPromotionScopeSchema,
  newScope: AvPromotionScopeSchema,
  newScopeKey: z.string().nullable(),
  frozenVersionId: z.string().nullable(),
});
export type AvPromoteResult = z.infer<typeof AvPromoteResultSchema>;

export const AvPromoteResultEnvelopeSchema = z.object({
  data: AvPromoteResultSchema,
});

export const KnowledgeSearchResultSchema = z.object({
  id: z.string(),
  kind: KnowledgeKindSchema,
  scope: KnowledgeScopeSchema,
  scopeKey: z.string().nullable(),
  source: z.string().nullable(),
  title: z.string(),
  snippet: z.string(),
  score: z.number(),
});

export const KnowledgeEntrySchema = z.object({
  id: z.string(),
  kind: KnowledgeKindSchema,
  scope: KnowledgeScopeSchema,
  scopeKey: z.string().nullable(),
  granularity: z.string().nullable(),
  intent: z.string(),
  payload: z.record(z.string(), z.unknown()),
  tags: z.array(z.string()),
  source: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  title: z.string(),
});

export const KnowledgeFilterOptionsSchema = z.object({
  kinds: z.array(KnowledgeKindSchema),
  scopes: z.array(KnowledgeScopeSchema),
});

export const KnowledgeSearchListEnvelopeSchema = z.object({
  data: z.array(KnowledgeSearchResultSchema).nullable(),
});

export const KnowledgeEntryEnvelopeSchema = z.object({
  data: KnowledgeEntrySchema,
});

export type KnowledgeKind = z.infer<typeof KnowledgeKindSchema>;
export type KnowledgeScope = z.infer<typeof KnowledgeScopeSchema>;
export type KnowledgeSearchResult = z.infer<typeof KnowledgeSearchResultSchema>;
export type KnowledgeEntry = z.infer<typeof KnowledgeEntrySchema>;
export type KnowledgeFilterOptions = z.infer<typeof KnowledgeFilterOptionsSchema>;

export interface KnowledgeSearchArgs {
  q: string;
  kinds?: KnowledgeKind[];
  activeDataset?: string | null;
  /**
   * Active agent-session id. When supplied, session-scoped entries owned
   * by the calling user inside that session participate in the result set.
   * The backend enforces ownership via the payload owner_user_id stamp.
   */
  sessionId?: string | null;
  limit?: number;
  minScore?: number;
}
